import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Trophy, Award } from 'lucide-react';

import { getTrainingLeaderboard } from '../../services/trainingService';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import PersonCell from '../../components/ui/PersonCell';
import GenericSelect from '../../components/ui/GenericSelect';

const Container = styled.div`padding: 16px 24px 24px;`;

const Card = styled.div`
  background: #fff;
  border-radius: 6px;
  padding: 20px;
  box-shadow: 0 1px 4px rgba(0, 66, 66, 0.06);
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const CardTitle = styled.h3`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #004242;
  margin: 0;
`;

const PeriodSelectWrapper = styled.div`
  width: 200px;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 48px 1fr auto auto;
  gap: 12px;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid #f1f5f9;
  &:last-child { border-bottom: none; }
`;

const RankBadge = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: 13px;
  color: ${({ $rank }) => $rank <= 3 ? '#fff' : '#64748b'};
  background: ${({ $rank }) =>
    $rank === 1 ? 'linear-gradient(135deg, #F59E0B, #FBBF24)' :
    $rank === 2 ? 'linear-gradient(135deg, #94A3B8, #CBD5E1)' :
    $rank === 3 ? 'linear-gradient(135deg, #B45309, #D97706)' :
    '#F1F5F9'};
`;

const PointsCell = styled.div`
  font-weight: 700;
  color: #0291EF;
  font-size: 15px;
`;

const BadgeCountCell = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #64748b;
  font-size: 12px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 48px 24px;
  color: #94a3b8;
`;

function formatPeriodLabel(period, t) {
  if (!period) return '';
  const [y, m] = period.split('-');
  const monthNames = t('common.months', { returnObjects: true });
  return `${monthNames?.[parseInt(m, 10) - 1] || m} ${y}`;
}

function generatePeriodOptions() {
  // Last 6 months + current
  const options = [];
  const now = new Date();
  for (let i = 0; i < 6; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    options.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  }
  return options;
}

export default function TrainingLeaderboardPage() {
  const { t } = useTranslation();
  const [period, setPeriod] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const periodOptions = generatePeriodOptions();
  const selectOptions = [
    { id: '', name: t('gamification.currentPeriod') },
    ...periodOptions.map((p) => ({ id: p, name: formatPeriodLabel(p, t) })),
  ];

  useEffect(() => {
    setLoading(true);
    getTrainingLeaderboard(period ? { period } : undefined)
      .then((res) => setData(res.data?.data))
      .finally(() => setLoading(false));
  }, [period]);

  return (
    <Container>
      <Breadcrumbs items={[{ label: t('gamification.leaderboard') }]} />

      <Card>
        <Header>
          <CardTitle>
            <Trophy size={16} style={{ verticalAlign: 'middle', marginRight: 6, color: '#F59E0B' }} />
            {t('gamification.leaderboardFor', { period: formatPeriodLabel(data?.period || period, t) })}
          </CardTitle>
          <PeriodSelectWrapper>
            <GenericSelect
              minimal
              options={selectOptions}
              value={period}
              onChange={(v) => setPeriod(v ?? '')}
              placeholder={t('gamification.currentPeriod')}
            />
          </PeriodSelectWrapper>
        </Header>

        {loading ? (
          <div style={{ color: '#94a3b8' }}>{t('common.loading')}</div>
        ) : !data?.entries || data.entries.length === 0 ? (
          <EmptyState>
            <Trophy size={42} style={{ marginBottom: 12, opacity: 0.4 }} />
            <div>{t('gamification.noLeaderboardData')}</div>
          </EmptyState>
        ) : (
          data.entries.map((e) => (
            <Row key={e.userId}>
              <RankBadge $rank={e.rank}>{e.rank}</RankBadge>
              <PersonCell name={e.fullName} profilePicture={e.profilePicture} />
              <BadgeCountCell>
                <Award size={14} />
                {e.badgeCount}
              </BadgeCountCell>
              <PointsCell>{e.points} {t('gamification.pointsShort')}</PointsCell>
            </Row>
          ))
        )}
      </Card>
    </Container>
  );
}
